/**
 * InteractiveCanvas を画像として書き出す。
 *
 * 制約:
 *   - 表示用 <img> は CORS 制限を避けるため crossOrigin 属性なし
 *   - そのままだと html-to-image 経由で canvas に描いた瞬間に tainted canvas エラー
 *   - 対策: 書き出し直前に「Firebase Storage SDK で getBlob → data URL」へ差し替え、終わったら戻す
 *
 * 流れ:
 *   1. data-export-target を取得
 *   2. 内部の全 <img> について、Firebase Storage URL なら getBlob で取得 → data URL に置換（src を一時差替）
 *   3. transform: scale を一時的に 1 に戻して原寸描画
 *   4. html-to-image で PNG / JPG 化
 *   5. すべての元 src と transform を復元
 */
import * as htmlToImage from 'html-to-image';
import { getBlob, ref as storageRef } from 'firebase/storage';

import { storage } from '@/lib/firebase';

function findTarget(slideId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-export-target="${slideId}"]`);
}

function triggerDownload(dataUrl: string, filename: string): void {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Firebase Storage の DownloadURL から { bucket, path } を抽出
 * 例: https://firebasestorage.googleapis.com/v0/b/caud-goenhub-dev.firebasestorage.app/o/marketing%2Fad-imgs%2Fxxx.png?alt=...
 */
function parseStorageUrl(url: string): { bucket: string; path: string } | null {
  try {
    const u = new URL(url);
    if (!u.host.includes('firebasestorage.googleapis.com')) return null;
    const m = u.pathname.match(/^\/v0\/b\/([^/]+)\/o\/(.+)$/);
    if (!m) return null;
    return { bucket: m[1], path: decodeURIComponent(m[2]) };
  } catch {
    return null;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });
}

/**
 * 書き出し対象 node 内で実際に使われている font-family を収集（stack の先頭のみ）。
 * Google Fonts CSS は 1 family が数十個の @font-face に分割されている (weight × unicode-range)
 * ため、使用フォントのみ embed しないと初回 fetch が数百リクエスト・数 MB に膨らむ。
 *
 * stack の先頭だけにするのは、実際にレンダリングに使われる primary font だけが必要なため。
 * 後続の fallback (Noto Sans JP, sans-serif 等) まで取ると、全ファミリーの全 woff2 が対象になる。
 */
const GENERIC_FAMILIES = new Set([
  'sans-serif', 'serif', 'monospace', 'system-ui', 'cursive', 'fantasy',
  'ui-sans-serif', 'ui-serif', 'ui-monospace', 'ui-rounded',
  'inherit', 'initial', 'unset', 'revert', 'revert-layer',
]);

function normalizeFamily(raw: string): string {
  return raw.replace(/["']/g, '').trim().toLowerCase();
}

function collectUsedFontFamilies(node: HTMLElement): Set<string> {
  const families = new Set<string>();
  const all = node.querySelectorAll<HTMLElement>('*');
  for (const el of Array.from(all)) {
    const ff = getComputedStyle(el).fontFamily;
    if (!ff) continue;
    const first = normalizeFamily(ff.split(',')[0] ?? '');
    if (first && !GENERIC_FAMILIES.has(first)) families.add(first);
  }
  return families;
}

/**
 * 書き出し対象 node 内で実際に表示される文字のコードポイント集合を収集。
 * unicode-range で分割された @font-face のうち、これらを含むものだけ embed すれば十分。
 */
function collectUsedCodePoints(node: HTMLElement): Set<number> {
  const set = new Set<number>();
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const text = n.textContent ?? '';
    for (const ch of text) {
      const cp = ch.codePointAt(0);
      if (cp != null) set.add(cp);
    }
  }
  return set;
}

/**
 * "U+0025-00FF, U+0131, U+30??" のような unicode-range 文字列を範囲配列にパース。
 * '?' はワイルドカード（U+30?? → 0x3000-0x30FF）。
 */
function parseUnicodeRange(text: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  for (const part of text.split(',').map((s) => s.trim())) {
    const m = part.match(/^U\+([0-9A-Fa-f?]+)(?:-([0-9A-Fa-f]+))?$/);
    if (!m) continue;
    const startStr = m[1];
    if (startStr.includes('?')) {
      const start = parseInt(startStr.replace(/\?/g, '0'), 16);
      const end = parseInt(startStr.replace(/\?/g, 'F'), 16);
      if (!Number.isNaN(start) && !Number.isNaN(end)) ranges.push([start, end]);
    } else {
      const start = parseInt(startStr, 16);
      const end = m[2] ? parseInt(m[2], 16) : start;
      if (!Number.isNaN(start) && !Number.isNaN(end)) ranges.push([start, end]);
    }
  }
  return ranges;
}

function ruleCoversAnyCodePoint(rule: CSSFontFaceRule, codePoints: Set<number>): boolean {
  const ur = rule.style.getPropertyValue('unicode-range');
  // unicode-range 指定なし = 全文字対象
  if (!ur) return true;
  const ranges = parseUnicodeRange(ur);
  if (ranges.length === 0) return true;
  for (const cp of codePoints) {
    for (const [s, e] of ranges) {
      if (cp >= s && cp <= e) return true;
    }
  }
  return false;
}

/**
 * document.styleSheets を走査して @font-face を抽出し、各 src URL を fetch + base64 化して
 * inline する。html-to-image の自前 embed が一部 Web フォントで失敗する（fallback フォントの
 * metrics 違いで「container < line-height」設計の要素が崩れる現象）の根本対策。
 *
 * - 使用中の font-family だけに絞って fetch コストを抑える
 * - 結果は font-family 単位で module スコープにキャッシュ
 */
// woff2 (URL) 単位でキャッシュ。書き出しごとに使い回されて network fetch を最小化する。
const woff2DataUrlCache = new Map<string, string>();
const woff2DataUrlPending = new Map<string, Promise<string | null>>();

async function fetchAsDataUrl(url: string): Promise<string | null> {
  if (woff2DataUrlCache.has(url)) return woff2DataUrlCache.get(url)!;
  if (woff2DataUrlPending.has(url)) return woff2DataUrlPending.get(url)!;
  const p = (async () => {
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) return null;
      const blob = await res.blob();
      const dataUrl = await blobToDataUrl(blob);
      woff2DataUrlCache.set(url, dataUrl);
      return dataUrl;
    } catch {
      return null;
    }
  })();
  woff2DataUrlPending.set(url, p);
  try {
    return await p;
  } finally {
    woff2DataUrlPending.delete(url);
  }
}

function extractFontFaceRuleFamily(rule: CSSFontFaceRule): string | null {
  const fam = rule.style.getPropertyValue('font-family');
  if (!fam) return null;
  return normalizeFamily(fam);
}

async function buildFontEmbedCSS(node: HTMLElement): Promise<string> {
  const families = collectUsedFontFamilies(node);
  if (families.size === 0) return '';
  const codePoints = collectUsedCodePoints(node);

  // family + 表示文字 codePoint でフィルタした @font-face rule を収集
  const matchedRuleTexts: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList | undefined;
    try {
      rules = sheet.cssRules ?? undefined;
    } catch {
      continue;
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSFontFaceRule)) continue;
      const ruleFam = extractFontFaceRuleFamily(rule);
      if (!ruleFam || !families.has(ruleFam)) continue;
      if (!ruleCoversAnyCodePoint(rule, codePoints)) continue;
      matchedRuleTexts.push(rule.cssText);
    }
  }

  // 各 @font-face の src URL を fetch して base64 化（URL は woff2DataUrlCache で共有）
  const urlRegex = /url\((['"]?)(https?:\/\/[^)'"]+)\1\)/g;
  const replaced: string[] = [];
  for (const text of matchedRuleTexts) {
    const urls: string[] = [];
    text.replace(urlRegex, (_m, _q, u: string) => {
      urls.push(u);
      return '';
    });
    await Promise.all(urls.map((u) => fetchAsDataUrl(u)));
    const rewritten = text.replace(urlRegex, (_m, _q, u: string) => {
      const data = woff2DataUrlCache.get(u);
      return data ? `url("${data}")` : `url("${u}")`;
    });
    replaced.push(rewritten);
  }

  return replaced.join('\n');
}

/** 画像 src を data URL 化。失敗時は元 URL のまま（描画は壊れる可能性あり）。 */
async function inlineImage(img: HTMLImageElement, cache: Map<string, string>): Promise<string | null> {
  const original = img.src;
  if (!original || original.startsWith('data:')) return null;
  if (cache.has(original)) {
    img.src = cache.get(original)!;
    return original;
  }
  const parsed = parseStorageUrl(original);
  if (!parsed) return null;
  try {
    // gs://{bucket}/{path} 形式で ref を作るとバケット不一致を回避
    const r = storageRef(storage, `gs://${parsed.bucket}/${parsed.path}`);
    const blob = await getBlob(r);
    const dataUrl = await blobToDataUrl(blob);
    cache.set(original, dataUrl);
    img.src = dataUrl;
    // ブラウザが新しい src を decode するのを待つ（タイミング差で空描画になるのを防ぐ）
    if (typeof img.decode === 'function') {
      try {
        await img.decode();
      } catch {
        /* decode 失敗は無視 */
      }
    }
    return original;
  } catch (e) {
    console.warn('inlineImage failed', { src: original, e });
    return null;
  }
}

export async function exportSlideAs(
  slideId: string,
  ext: 'png' | 'jpg',
  filename: string,
): Promise<void> {
  const node = findTarget(slideId);
  if (!node) throw new Error('Canvas target not found');

  // 1) 全 <img> を data URL 化（CORS 回避）
  const cache = new Map<string, string>();
  const imgs = Array.from(node.querySelectorAll('img'));
  const restore: Array<{ img: HTMLImageElement; src: string }> = [];
  for (const img of imgs) {
    const original = await inlineImage(img, cache);
    if (original) restore.push({ img, src: original });
  }

  // 2) 編集 UI を一時的に隠す
  //   - 選択枠 (data-element-frame の outline)
  //   - リサイズハンドル (style.background = var(--accent) の小さい四角)
  //   - 画像未配置 placeholder の点線枠と「挿入」UI
  const hiddenStyleEl = document.createElement('style');
  hiddenStyleEl.dataset.exportTemp = '1';
  hiddenStyleEl.textContent = `
    [data-export-target="${slideId}"] [data-element-frame] { outline: none !important; }
    [data-export-target="${slideId}"] [data-image-placeholder] { display: none !important; }
  `;
  document.head.appendChild(hiddenStyleEl);

  // 3) 一時的に scale(1) で原寸描画
  const originalTransform = node.style.transform;
  node.style.transform = 'scale(1)';
  await new Promise((r) => requestAnimationFrame(() => r(null)));

  const width = node.offsetWidth;
  const height = node.offsetHeight;

  // フォント embed を確実に効かせるため、@font-face を自前で inline 化して渡す
  // （内部の auto-embed は一部 Web フォントで失敗し、fallback フォントの metrics 違いで
  //  「container < line-height」設計の要素が崩れる）
  // 実際に使われている font-family のみ対象にしてコストを抑える
  const fontEmbedCSS = await buildFontEmbedCSS(node);

  const options: Parameters<typeof htmlToImage.toPng>[1] = {
    width,
    height,
    pixelRatio: 1,
    cacheBust: false,
    fontEmbedCSS,
    style: { transform: 'scale(1)', transformOrigin: 'top left' },
  };

  try {
    const dataUrl =
      ext === 'png'
        ? await htmlToImage.toPng(node, options)
        : await htmlToImage.toJpeg(node, { ...options, quality: 0.92, backgroundColor: '#ffffff' });
    triggerDownload(dataUrl, filename);
  } finally {
    // 復元
    node.style.transform = originalTransform;
    for (const r of restore) r.img.src = r.src;
    hiddenStyleEl.remove();
  }
}

/**
 * Phase 41: 編集中キャンバスのサムネイル (低画質 JPG dataURL) を生成。
 * - 幅 400px / quality 0.55 で軽量化
 * - 編集 UI（選択枠・ハンドル・placeholder）は exportSlideAs と同様に非表示化
 * - 失敗時は null
 */
/**
 * Phase 41 採用中止: html-to-image によるサムネイル化は Google Fonts のインライン化処理で
 * 30 秒+のハングが発生する（filter / skipFonts / fontEmbedCSS いずれも効果なし）。
 * 代わりに AdImagesListPage で SlideThumbnail コンポーネントを使って直接 DOM を描画する。
 * 関数自体は将来の API 経由サーバサイド生成に置き換える際の type signature 用に残置。
 */
export async function generateSlideThumbnail(
  _slideId: string,
  _targetWidth = 400,
): Promise<string | null> {
  void _slideId;
  void _targetWidth;
  return null;
}
